// src/components/EmbeddedSignupButton.js
//
// Componente isolado e reusável que executa o fluxo Embedded Signup da
// Meta WhatsApp Business (Cetto Tech Provider — app 5418).
//
// Fluxo end-to-end (alinhado ao PDF Seções 4 e 5):
//
//   1. No mount, busca /onboarding/embedded-signup/config pra saber o
//      config_id, app_id, featureType e a versão da Graph (auth-gated —
//      só roda se o user tiver token).
//   2. Lazy-loads o FB JS SDK (https://connect.facebook.net/en_US/sdk.js).
//      Não tocamos no index.html — script só entra no DOM quando o
//      componente é renderizado.
//   3. Cliente clica → FB.login({config_id, response_type:'code', ...})
//      abre o popup oficial Meta (domínio facebook.com). Cliente faz
//      login → seleciona/cria BM → escolhe número → verifica SMS →
//      setup PIN.
//
//      set/26 — COEXISTÊNCIA: `extras.featureType` agora vai como
//      'whatsapp_business_app_onboarding' em vez de string vazia. Com a
//      string vazia a Meta abre o onboarding padrão (número novo /
//      migração) e recusa qualquer número que já esteja ativo no
//      WhatsApp Business App com o erro #2494064. Ver o comentário da
//      constante FEATURE_TYPE_COEXISTENCE mais abaixo.
//   4. Captura dos dados acontece em DOIS canais assíncronos:
//        a) authResponse.code vem no CALLBACK do FB.login
//        b) phone_number_id + waba_id vêm via window.postMessage
//           (event.type === 'WA_EMBEDDED_SIGNUP', event começando com
//           'FINISH' — o sufixo varia por fluxo, ver o listener)
//      Como a ordem é incerta, usamos refs e disparamos a chamada ao
//      backend só quando AMBOS chegarem (tryFinalize). Se só um chegar,
//      um watchdog de 90s corta a espera com mensagem em vez de deixar a
//      tela parada.
//   5. POST /onboarding/whatsapp-callback com {code, phone_number_id,
//      waba_id}. Backend troca o code por access_token, cria Device,
//      retorna sucesso.
//
// Como usar:
//
//   import EmbeddedSignupButton from '../components/EmbeddedSignupButton';
//
//   <EmbeddedSignupButton
//     onSuccess={(device) => {
//       // device = { status, device_id, phone_number, display_name, message }
//       navigate('/dashboard');
//     }}
//     onError={(err) => {
//       console.error(err);
//     }}
//   />
//
// Estados visuais:
//   loading-config → spinner enquanto carrega /config + SDK
//   idle           → botão verde "Conectar seu WhatsApp agora"
//   opening        → "Abrindo Meta..." (popup abrindo)
//   processing     → "Conectando seu número..." (backend processando)
//   done           → "✅ Conectado" (com nome do número)
//   error          → mensagem + botão "Tentar novamente"
//
// Pra Embedded Signup funcionar localmente, o domínio precisa estar em
// "App Domains" no painel Meta. Localhost não funciona — testes têm que
// rodar em produção (cettosystem.com.br) ou num staging com domínio
// próprio cadastrado.

import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { FiSmartphone, FiCheckCircle, FiAlertCircle, FiArrowRight } from 'react-icons/fi';
import api from '../services/Api';

// ─── Constantes ─────────────────────────────────────────────────────────────
const FB_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';
// Fallbacks. O valor real vem de /onboarding/embedded-signup/config, pra
// trocar por env no droplet sem redeploy da Vercel.
const FB_API_VERSION_FALLBACK = 'v18.0';

// extras.featureType do FB.login — decide QUAL fluxo a Meta abre.
//
// Até set/26 isso ia como string vazia, que significa onboarding padrão
// (registrar número novo / migrar). Quando o cliente tentava conectar um
// número que já estava rodando no WhatsApp Business App, a Meta lia o
// pedido como "portar a linha pra fora do celular", via a conta ativa no
// app e barrava com:
//
//   #2494064 — "Esse número de telefone já está registrado em uma conta
//   do WhatsApp. Para usá-lo, desconecte-o da conta existente..."
//
// 'whatsapp_business_app_onboarding' é o fluxo de COEXISTÊNCIA: o número
// continua no app do celular e ganha a Cloud API em paralelo, com sync de
// histórico. É o que o produto vende.
//
// O featureType sozinho NÃO basta: o `config_id` também precisa ter sido
// criado no painel da Meta com Coexistência habilitada. Os dois têm que
// combinar, senão o #2494064 volta.
const FEATURE_TYPE_COEXISTENCE = 'whatsapp_business_app_onboarding';

// Janela do watchdog do segundo sinal. 90s é folgado pra um evento que
// deveria chegar em segundos depois do primeiro, e curto o bastante pra
// o cliente não achar que travou.
const WATCHDOG_MS = 90_000;

// ─── Loader idempotente do FB SDK ──────────────────────────────────────────
// Retorna uma Promise que resolve quando window.FB está pronto. Múltiplas
// chamadas reaproveitam o mesmo carregamento.
let _fbLoadPromise = null;

function loadFBSdk(appId, graphVersion) {
  if (_fbLoadPromise) return _fbLoadPromise;
  _fbLoadPromise = new Promise((resolve, reject) => {
    // Se já carregou e está inicializado, devolve direto
    if (window.FB && window.FB.__cettoInitialized) {
      resolve(window.FB);
      return;
    }
    // Inicializador chamado pelo SDK quando carrega
    window.fbAsyncInit = function () {
      try {
        window.FB.init({
          appId,
          autoLogAppEvents: true,
          xfbml: false,
          version: graphVersion || FB_API_VERSION_FALLBACK,
        });
        window.FB.__cettoInitialized = true;
        resolve(window.FB);
      } catch (e) {
        reject(e);
      }
    };
    // Injeta o script só uma vez
    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = FB_SDK_URL;
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.onerror = () => reject(new Error('Falha ao carregar SDK do Facebook.'));
      document.body.appendChild(script);
    }
  });
  return _fbLoadPromise;
}

// ─── Componente ────────────────────────────────────────────────────────────
export default function EmbeddedSignupButton({ onSuccess, onError, deviceName }) {
  // NOTA: `config.feature_type` (vindo do backend) decide o fluxo. Quando
  // é Coexistência, o número PRECISA estar ativo no WhatsApp Business App
  // do celular — a sub-label no fim do arquivo avisa o cliente disso antes
  // do clique, porque o erro correspondente só apareceria dentro do popup
  // da Meta, onde não conseguimos interceptar nem traduzir.
  // Estado da máquina de estados (loading-config → idle → opening → processing → done|error)
  const [phase, setPhase] = useState('loading-config');
  const [errorMsg, setErrorMsg] = useState(null);
  const [result, setResult] = useState(null);

  // Config vinda do backend (config_id + app_id)
  const [config, setConfig] = useState(null);

  // Refs pra capturar code + waba_data race-safe.
  // Os dois eventos podem chegar em qualquer ordem; tryFinalize só dispara
  // o POST quando ambos estão disponíveis. Refs em vez de state pra não
  // re-renderizar e perder closure.
  const codeRef = useRef(null);
  const wabaRef = useRef(null);

  // Watchdog do segundo sinal. Os dois canais (authResponse.code e o
  // WA_EMBEDDED_SIGNUP) são independentes; se UM chega e o outro não, o
  // componente ficaria em "Abrindo Meta..." pra sempre, que foi parte do
  // que tornou o bug do FINISH tão difícil de enxergar — a tela não dizia
  // nada. O timer só arma DEPOIS do primeiro sinal: antes disso a espera
  // é legítima e pode levar minutos (o cliente ainda está lendo o QR
  // code, escolhendo BM, esperando os 45s de conexão da Meta).
  const watchdogRef = useRef(null);
  const clearWatchdog = useCallback(() => {
    if (watchdogRef.current) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, []);

  // ─── 1. Boot: busca config + carrega SDK ───────────────────────────────
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/onboarding/embedded-signup/config');
        if (!mounted) return;
        if (!res.data.enabled) {
          setPhase('error');
          setErrorMsg('Embedded Signup ainda não está habilitado nesta conta. Fale com o suporte.');
          return;
        }
        setConfig(res.data);
        try {
          await loadFBSdk(res.data.app_id, res.data.graph_version);
          if (mounted) setPhase('idle');
        } catch (sdkErr) {
          if (mounted) {
            setPhase('error');
            setErrorMsg('Não foi possível carregar o SDK do Facebook. Atualize a página.');
          }
        }
      } catch (cfgErr) {
        if (mounted) {
          setPhase('error');
          setErrorMsg(cfgErr.response?.data?.detail || 'Não foi possível carregar a configuração.');
        }
      }
    })();
    return () => { mounted = false; };
  }, []);

  // ─── 2. tryFinalize: dispara POST quando code+waba_data já chegaram ───
  // Definida com useRef pro listener de window.message poder chamar sem
  // closure stale (o handler é criado uma vez no mount).
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const deviceNameRef = useRef(deviceName);
  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);
  useEffect(() => { deviceNameRef.current = deviceName; }, [deviceName]);

  const tryFinalize = useCallback(async () => {
    const code = codeRef.current;
    const waba = wabaRef.current;
    if (!code || !waba || !waba.phone_number_id || !waba.waba_id) {
      // Ainda esperando o outro evento — arma o watchdog (ver comentário
      // na declaração de watchdogRef). Idempotente: só o primeiro arma.
      if (!watchdogRef.current) {
        watchdogRef.current = setTimeout(() => {
          watchdogRef.current = null;
          const faltou = !codeRef.current ? 'a autorização do Facebook' : 'a confirmação do número';
          console.warn('[EmbeddedSignup] watchdog: faltou', faltou);
          codeRef.current = null;
          wabaRef.current = null;
          setErrorMsg(
            `A Meta não devolveu ${faltou}. Isso costuma ser popup fechado cedo demais `
            + 'ou bloqueador de anúncios. Tente de novo mantendo a janela aberta até o fim.',
          );
          setPhase('error');
        }, WATCHDOG_MS);
      }
      return;
    }
    // Limpa refs ANTES da chamada pra evitar duplo-fire
    clearWatchdog();
    codeRef.current = null;
    wabaRef.current = null;

    setPhase('processing');
    try {
      const res = await api.post('/onboarding/whatsapp-callback', {
        code,
        phone_number_id: waba.phone_number_id,
        waba_id: waba.waba_id,
        device_name: deviceNameRef.current || undefined,
      });
      setResult(res.data);
      setPhase('done');
      onSuccessRef.current?.(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao conectar o número.';
      setErrorMsg(detail);
      setPhase('error');
      onErrorRef.current?.(err);
    }
  }, [clearWatchdog]);

  // ─── 3. Listener global pro WA_EMBEDDED_SIGNUP message ─────────────────
  // Meta envia via window.postMessage com event.origin facebook.com.
  //
  // O NOME DO EVENTO DE SUCESSO MUDA CONFORME O FLUXO — e isso custou um
  // bug silencioso (set/26). Os que a Meta emite:
  //
  //   FINISH
  //       onboarding padrão concluído (número novo / migração).
  //   FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING
  //       COEXISTÊNCIA concluída. Nome diferente, mesmo payload.
  //   FINISH_ONLY_WABA
  //       o cliente criou/escolheu a WABA mas NÃO chegou a vincular um
  //       número. Vem sem phone_number_id — não dá pra criar Device.
  //   CANCEL / ERROR
  //
  // Até set/26 o handler comparava `=== 'FINISH'` e só isso. No fluxo de
  // Coexistência o evento chegava como FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING,
  // caía fora do if, `wabaRef` nunca era preenchido, `tryFinalize` nunca
  // disparava — e o popup simplesmente fechava depois do QR code sem
  // nenhum erro, sem chamada ao backend e sem chip criado. Por isso o
  // casamento agora é por PREFIXO 'FINISH', não igualdade.
  useEffect(() => {
    const handler = (event) => {
      const okOrigin = event.origin === 'https://www.facebook.com'
        || event.origin === 'https://web.facebook.com'
        || event.origin.endsWith('.facebook.com');
      if (!okOrigin) return;

      let payload;
      try {
        payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') return;

      const evt = String(payload.event || '');
      const data = payload.data || {};

      // Rastro no console pra diagnosticar sem depender de adivinhação:
      // o bug acima era invisível justamente porque nada era registrado.
      // Só nome do evento e presença dos ids (não o conteúdo) — nada de
      // telefone do cliente em log, regra 4 do CLAUDE.md.
      console.info(
        '[EmbeddedSignup] evento Meta:', evt,
        '| phone_number_id:', data.phone_number_id || '(ausente)',
        '| waba_id:', data.waba_id || '(ausente)',
      );

      if (evt.startsWith('FINISH')) {
        if (!data.phone_number_id || !data.waba_id) {
          // FINISH_ONLY_WABA e afins: fluxo terminou sem número vinculado.
          // Falhar com mensagem clara é melhor que ficar esperando pra
          // sempre um segundo evento que não vem.
          codeRef.current = null;
          wabaRef.current = null;
          setErrorMsg(
            'A conta da Meta foi criada, mas nenhum número foi vinculado. '
            + 'Abra o fluxo de novo e conclua a etapa de seleção do número.',
          );
          setPhase('error');
          return;
        }
        wabaRef.current = {
          phone_number_id: data.phone_number_id,
          waba_id: data.waba_id,
        };
        tryFinalize();
      } else if (evt === 'CANCEL') {
        // Cliente fechou o popup antes de terminar. `current_step` diz
        // onde parou — útil no suporte pra saber se travou no QR code,
        // na seleção de número, etc.
        console.info('[EmbeddedSignup] cancelado em:', data.current_step || '(desconhecido)');
        clearWatchdog();
        codeRef.current = null;
        wabaRef.current = null;
        setPhase('idle');
      } else if (evt === 'ERROR') {
        clearWatchdog();
        codeRef.current = null;
        wabaRef.current = null;
        setErrorMsg(
          data.error_message
            || 'A Meta recusou a conexão. Confira se o número está ativo no WhatsApp Business do celular.',
        );
        setPhase('error');
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [tryFinalize, clearWatchdog]);

  // ─── 4. Click handler: dispara FB.login ────────────────────────────────
  const handleClick = useCallback(() => {
    if (!window.FB || !config?.config_id) return;
    // Reset state pra retry
    clearWatchdog();
    codeRef.current = null;
    wabaRef.current = null;
    setErrorMsg(null);
    setPhase('opening');

    window.FB.login(
      (response) => {
        // Esse callback é o "OAuth code returned" pathway.
        if (response.authResponse?.code) {
          codeRef.current = response.authResponse.code;
          tryFinalize();
        } else if (response.status === 'unknown') {
          // Pop-up bloqueado ou erro de SDK
          setPhase('error');
          setErrorMsg('Não foi possível abrir o popup. Permita pop-ups e tente de novo.');
        } else {
          // Cancelado pelo usuário — sem disparar WA_EMBEDDED_SIGNUP/CANCEL
          // (acontece quando ele fecha a janela antes de chegar nos passos finais).
          setPhase('idle');
        }
      },
      {
        config_id: config.config_id,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          // Coexistência. `config.feature_type` vem do backend
          // (META_EMBEDDED_SIGNUP_FEATURE_TYPE) pra dar pra voltar ao
          // onboarding padrão sem redeploy; o `??` deixa o valor do
          // backend vencer inclusive quando for string vazia — é assim
          // que se desliga a Coexistência de propósito. Só cai no
          // fallback se o campo não existir na resposta (backend antigo).
          featureType: config.feature_type ?? FEATURE_TYPE_COEXISTENCE,
          // sessionInfoVersion: '3' garante que recebemos eventos WA_EMBEDDED_SIGNUP
          // com phone_number_id/waba_id no payload (versões antigas não tinham).
          // String, não número — é o que a doc da Meta especifica.
          sessionInfoVersion: '3',
        },
      },
    );
  }, [config, tryFinalize, clearWatchdog]);

  // Timer pendurado num componente desmontado dispararia setState em nada.
  useEffect(() => clearWatchdog, [clearWatchdog]);

  // ─── Render ────────────────────────────────────────────────────────────

  if (phase === 'loading-config') {
    return (
      <Box sx={loadingBoxSx}>
        <CircularProgress size={20} sx={{ color: '#22c55e' }} />
        <Typography sx={hintTextSx}>Carregando integração com a Meta...</Typography>
      </Box>
    );
  }

  if (phase === 'done' && result) {
    return (
      <Box sx={{ ...resultBoxSx, borderColor: 'rgba(34,197,94,0.4)', bgcolor: 'rgba(34,197,94,0.08)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FiCheckCircle size={24} color="#22c55e" />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#f0fdf4' }}>
              WhatsApp conectado!
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.65)' }}>
              {result.display_name || 'Número ativo'}
              {result.phone_number ? ` · +${result.phone_number}` : ''}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  if (phase === 'error') {
    return (
      <Box sx={{ ...resultBoxSx, borderColor: 'rgba(239,68,68,0.4)', bgcolor: 'rgba(239,68,68,0.08)' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
          <FiAlertCircle size={22} color="#ef4444" style={{ marginTop: 2, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.9)', lineHeight: 1.55 }}>
            {errorMsg || 'Não foi possível conectar agora.'}
          </Typography>
        </Box>
        <Box
          onClick={() => { setErrorMsg(null); setPhase('idle'); }}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700,
            color: '#22c55e', textDecoration: 'underline',
            '&:hover': { color: '#16a34a' },
          }}
        >
          Tentar novamente <FiArrowRight size={13} />
        </Box>
      </Box>
    );
  }

  // 'idle' | 'opening' | 'processing'
  const isLoading = phase === 'opening' || phase === 'processing';
  const isCoexistence =
    (config?.feature_type ?? FEATURE_TYPE_COEXISTENCE) === FEATURE_TYPE_COEXISTENCE;
  const label =
    phase === 'opening' ? 'Abrindo Meta...'
    : phase === 'processing' ? 'Conectando seu número...'
    : 'Conectar seu WhatsApp agora';

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        component="button"
        onClick={isLoading ? undefined : handleClick}
        disabled={isLoading}
        sx={{
          width: '100%',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 1.2,
          bgcolor: isLoading ? 'rgba(34,197,94,0.55)' : '#22c55e',
          color: '#0A0F1A',
          border: 'none',
          borderRadius: '10px',
          padding: '14px 28px',
          fontFamily: "'Syne', sans-serif",
          fontSize: '0.95rem',
          fontWeight: 700,
          letterSpacing: '0.02em',
          cursor: isLoading ? 'default' : 'pointer',
          transition: 'all 0.15s ease',
          boxShadow: isLoading ? 'none' : '0 4px 16px rgba(34,197,94,0.28)',
          '&:hover': {
            bgcolor: isLoading ? 'rgba(34,197,94,0.55)' : '#16a34a',
            boxShadow: isLoading ? 'none' : '0 6px 20px rgba(34,197,94,0.38)',
          },
        }}
      >
        {isLoading ? (
          <CircularProgress size={18} sx={{ color: '#0A0F1A' }} />
        ) : (
          <FiSmartphone size={18} />
        )}
        {label}
      </Box>

      {/* Pré-requisito da Coexistência. Fica ANTES do selo porque é a
          informação que evita o erro: se o número não estiver ativo no app
          do celular, a Meta recusa dentro do popup (#2494064) e não temos
          como traduzir a mensagem de lá. Só aparece no fluxo de
          Coexistência — no onboarding padrão a exigência é o oposto. */}
      {isCoexistence && (
        <Box sx={{
          display: 'flex', alignItems: 'flex-start', gap: 0.8,
          mt: 1.2, padding: '8px 12px',
          bgcolor: 'rgba(34,197,94,0.06)',
          border: '1px solid rgba(34,197,94,0.18)',
          borderRadius: '8px',
          fontSize: '0.72rem', lineHeight: 1.5,
          color: 'rgba(255,255,255,0.62)',
          fontFamily: "'Syne', sans-serif",
        }}>
          <FiSmartphone size={13} style={{ marginTop: 2, flexShrink: 0, color: '#22c55e' }} />
          <span>
            O número precisa estar <strong style={{ color: 'rgba(255,255,255,0.85)' }}>ativo
            no WhatsApp Business do celular</strong>. Ele continua funcionando lá —
            a Cetto entra em paralelo, no mesmo número.
          </span>
        </Box>
      )}

      {/* Sub-label discreta com o "selo" Meta — reforça que o popup é oficial */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.6,
        mt: 1.2, fontSize: '0.7rem', color: 'rgba(255,255,255,0.42)',
        fontFamily: "'Syne', sans-serif",
      }}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1877F2' }} />
        Fluxo oficial Meta · domínio facebook.com · seus dados não passam pela Cetto
      </Box>
    </Box>
  );
}

// ─── Estilos compartilhados ────────────────────────────────────────────────

const loadingBoxSx = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
  padding: '14px 20px',
  bgcolor: 'rgba(255,255,255,0.04)',
  border: '1px dashed rgba(255,255,255,0.15)',
  borderRadius: '10px',
};

const hintTextSx = {
  fontFamily: "'Syne', sans-serif",
  fontSize: '0.82rem',
  color: 'rgba(255,255,255,0.6)',
};

const resultBoxSx = {
  padding: '14px 18px',
  border: '1px solid',
  borderRadius: '10px',
};
